'use client'
import { useEffect, useState, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import { selectAuth, refreshAuthUser, clearAuthUser } from '@/src/redux/slices/authSlice'
import { useLazyUserLogoutQuery } from '@/src/api/rtkQuery/userApi'
import { deleteCookie } from '@/src/utils/browser'

export interface UseHeaderAuthReturn {
    user: ReturnType<typeof selectAuth>
    handleSignOut: () => Promise<void>
    isMounted: boolean
}

/** Hook for header authentication state and sign-out logic */
export const useHeaderAuth = (): UseHeaderAuthReturn => {
    const [isMounted, setIsMounted] = useState(false)
    const dispatch = useAppDispatch()
    const user = useAppSelector(selectAuth)
    const [triggerLogout] = useLazyUserLogoutQuery()

    useEffect(() => {
        if (typeof window === 'undefined') return
        dispatch(refreshAuthUser())
        setIsMounted(true)
    }, [dispatch])

    const handleSignOut = useCallback(async () => {
        try {
            await triggerLogout()?.unwrap()
            dispatch(clearAuthUser())
            const domain = '.' + window.location.hostname
            deleteCookie('usermisc', domain)
            deleteCookie('userid', domain)
            deleteCookie('userData', domain)
            dispatch(refreshAuthUser())
        } catch (error) {
            console.error('Logout failed:', error)
        }
    }, [dispatch, triggerLogout])

    return {
        user,
        handleSignOut,
        isMounted
    }
}
